/**
 * deriveScheduleUiState — single source of truth for a schedule card's visuals.
 *
 * Maps ``(enabled, isClock, lastFireStatus)`` to everything the UI renders so
 * every state is handled in ONE place (not scattered ternaries). The bug this
 * fixes: the power button keyed on ``enabled`` and showed "Stop" for an armed
 * clock-less schedule that WASN'T running (e.g. its kickoff failed) — a dead
 * end. The correct signal for the button is "is a run in flight?", which for a
 * clock-less schedule is ``lastFireStatus === 'STARTED'`` (the only non-terminal
 * run status the trigger/loop write).
 *
 * Run statuses the backend emits (scheduler_trigger + scheduler_loop_controller):
 *   STARTED         — a run is in flight (NON-terminal)
 *   ACHIEVED        — loop hit its objective (terminal, success)
 *   MAX_TURNS       — loop hit its turn cap (terminal, neutral/done)
 *   DEADLINE        — loop stopped at its deadline (terminal, neutral)
 *   NO_PROGRESS     — loop stopped: repeated identical ratings (terminal, warn)
 *   BUDGET_BLOCKED  — budget gate refused the run (terminal, warn)
 *   ROOM_BUSY_SKIP  — skipped: room already running (terminal, info)
 *   DISABLED_SKIP   — skipped: schedule disabled at fire time (terminal, info)
 *   JUDGED          — a turn was judged mid-loop (transient, treated as running)
 *   KICKOFF_FAILED  — the run failed to start / errored (terminal, ERROR)
 */

// Non-terminal statuses = a run is actively happening.
const RUNNING_STATUSES = new Set(['STARTED', 'JUDGED']);

// Terminal statuses that are a FAILURE the user should notice + act on.
const FAILURE_STATUSES = new Set(['KICKOFF_FAILED']);

// Terminal statuses that are a soft/attention stop (not an error, but not a
// clean success — the user may want to know why it stopped).
const WARN_STATUSES = new Set(['BUDGET_BLOCKED', 'NO_PROGRESS']);

// Terminal statuses that are a clean/expected completion.
const SUCCESS_STATUSES = new Set(['ACHIEVED', 'MAX_TURNS', 'DEADLINE']);

// Terminal statuses that mean "we intentionally skipped this fire".
const SKIP_STATUSES = new Set(['ROOM_BUSY_SKIP', 'DISABLED_SKIP']);

/** Human, lower-stakes labels for each status (shown on the card subline). */
const STATUS_LABEL = {
  STARTED: 'Running…',
  JUDGED: 'Running… (judging)',
  ACHIEVED: 'Objective met',
  MAX_TURNS: 'Done (max turns)',
  DEADLINE: 'Stopped (deadline)',
  NO_PROGRESS: 'Stopped (no progress)',
  BUDGET_BLOCKED: 'Budget blocked',
  ROOM_BUSY_SKIP: 'Skipped (room busy)',
  DISABLED_SKIP: 'Skipped (disabled)',
  KICKOFF_FAILED: 'Failed to start',
};

/**
 * @param {{enabled?: boolean, isClock?: boolean, lastFireStatus?: string}} args
 * @returns {{
 *   isRunning: boolean,      // a run is in flight
 *   buttonMode: 'START'|'STOP',  // what the power button does
 *   dotClass: string,        // status dot color
 *   tone: 'running'|'success'|'warn'|'error'|'skip'|'idle'|'paused',
 *   statusLabel: string,     // '' when nothing to show
 *   needsAttention: boolean, // show the ⚠️ + surface the reason
 * }}
 */
export function deriveScheduleUiState({
  enabled = false, isClock = false, lastFireStatus = '', liveRunning = false,
} = {}) {
  const status = String(lastFireStatus || '').toUpperCase();
  // ``liveRunning`` is an authoritative override from the live channel
  // (UI_STATE=RUNNING for this pool). It wins over the denormalized
  // ``lastFireStatus`` — which can lag or be clobbered by a ROOM_BUSY_SKIP
  // written after a STARTED run began. If the room is live-running, the card
  // must show Running + a STOP button regardless of the stale denorm.
  const isRunning = liveRunning || RUNNING_STATUSES.has(status);

  // Button: STOP only when a run is actually in flight (clock-less) OR the clock
  // is armed (cadence). Everything else — including an enabled-but-idle
  // clock-less schedule whose last run finished/failed — offers START so the
  // user can (re)launch. This is the fix for the "stuck on Stop" dead end.
  let buttonMode;
  if (isClock) {
    buttonMode = enabled ? 'STOP' : 'START';   // clock armed = STOP (disarm)
  } else {
    buttonMode = isRunning ? 'STOP' : 'START'; // clock-less: only a live run is STOP-able
  }

  // Tone + dot: reflect the real outcome of the last fire.
  let tone;
  let dot;
  if (isRunning) {
    tone = 'running'; dot = 'bg-emerald-500 animate-pulse';
  } else if (FAILURE_STATUSES.has(status)) {
    tone = 'error'; dot = 'bg-red-500';
  } else if (WARN_STATUSES.has(status)) {
    tone = 'warn'; dot = 'bg-amber-500';
  } else if (SKIP_STATUSES.has(status)) {
    tone = 'skip'; dot = 'bg-slate-400';
  } else if (SUCCESS_STATUSES.has(status)) {
    tone = 'success'; dot = 'bg-emerald-500';
  } else if (!enabled) {
    tone = 'paused'; dot = 'bg-gray-300';
  } else {
    tone = 'idle'; dot = enabled ? 'bg-emerald-500' : 'bg-gray-300';
  }

  // Paused always dims the dot (unless a run is somehow still in flight).
  if (!enabled && !isRunning) dot = 'bg-gray-300';

  // When the live channel says running, the headline is "Running…" — the denorm
  // status label (which may be a stale skip/terminal) must not leak through.
  const statusLabel = isRunning ? STATUS_LABEL.STARTED : (STATUS_LABEL[status] || '');

  return {
    isRunning,
    buttonMode,
    dotClass: dot,
    tone,
    statusLabel,
    // The user should be nudged on a real failure or a soft-stop while enabled —
    // but never while a run is live (running wins).
    needsAttention: !isRunning && (tone === 'error' || (enabled && tone === 'warn')),
  };
}

export const SCHEDULE_STATUS_LABEL = STATUS_LABEL;
